import { can, clientName, formatDate } from '@clim/client-core';
import {
  amountsVisibility,
  CUSTOMER_TYPE_LABELS,
  DOSSIER_EDITABLE,
  DOSSIER_EDITABLE_BY_CONTROL,
  SALE_CHANNELS,
  SALE_CHANNEL_LABELS,
  SIGNER_QUALITY_LABELS,
  UNIT_LABELS,
  customerTypeOf,
  formatEuros,
  legalVars,
  renderLegalText,
  withdrawalRight,
  type CompanyLegal,
  type ContractContent,
  type DossierRecord,
  type SaleChannel,
  type Strokes,
} from '@clim/domain';
import { useMemo, useState } from 'react';
import { errorMessage, fieldErrors } from '../lib/errors';
import { useEngine, useReady } from '../lib/session';
import { SignaturePad } from './SignaturePad';
import { Empty, ErrorBox, Field, Input, Modal, Select, Textarea } from './ui';

const ACCESS = { none: 'aucun', ladder: 'échelle', nacelle: 'nacelle' } as Record<string, string>;

/**
 * Conditions de la vente (rétractation), réserves (§4.11), récapitulatif + cases (§4.12),
 * signature et contrat figé (§4.13) — pack juridique Prestige Habitat.
 */
export function ContractPanel({ dossier }: { dossier: DossierRecord }) {
  const engine = useEngine();
  const { me, api } = useReady();
  const [signing, setSigning] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [comment, setComment] = useState('');
  const d = dossier;
  const company = (me.tenant.settings as { legal?: CompanyLegal }).legal;
  // champs facultatifs réglés par l'administrateur (masqués par défaut ; les valeurs enregistrées sont conservées)
  const form = me.tenant.settings.dossierForm ?? { showSaleChannel: false, showProActivity: false };
  const client = engine.get('client', d.clientId);
  const customerType = customerTypeOf(client?.kind ?? 'individual');
  const w = withdrawalRight(
    {
      customerType,
      saleChannel: d.saleChannel,
      proEmployeeCount: d.proEmployeeCount,
      proOutsideMainActivity: d.proOutsideMainActivity,
    },
    company,
  );
  const templates = engine.legalTexts('reserve');
  const editable =
    !d.signature &&
    ((DOSSIER_EDITABLE.includes(d.status) && can(me, 'dossier.update')) ||
      (DOSSIER_EDITABLE_BY_CONTROL.includes(d.status) && can(me, 'dossier.control')));
  const canSign = !d.signature && ['draft', 'to_correct'].includes(d.status) && can(me, 'signature.collect');
  // administrateur (signature.manage) : annuler, corriger ou supprimer quel que soit le statut du dossier
  const canManage = !!d.signature && can(me, 'signature.manage');
  const canVoid =
    !!d.signature &&
    (canManage ||
      (['draft', 'submitted', 'to_correct'].includes(d.status) &&
        (can(me, 'dossier.control') || (d.status === 'draft' && can(me, 'signature.collect')))));
  const [managing, setManaging] = useState<'edit' | 'delete' | null>(null);
  // devis non signé à envoyer au client (il voit l'offre avant de signer)
  const [quoting, setQuoting] = useState(false);
  const canQuote =
    !d.signature &&
    d.status !== 'cancelled' &&
    can(me, 'signature.collect') &&
    (can(me, 'dossier.amounts.read') || can(me, 'payment.collect'));
  const run = (p: Promise<unknown>) => {
    setError(null);
    return p.catch((e) => setError(errorMessage(e)));
  };
  const wall = d.installWallTypeId ? engine.get('reason', d.installWallTypeId)?.label : null;
  const vars = (c: string | null) =>
    legalVars(company, {
      'dossier.support': wall,
      'dossier.hauteur': d.installHeightM === null ? null : `${String(d.installHeightM).replace('.', ',')} m`,
      'dossier.acces': d.installAccess ? ACCESS[d.installAccess] : null,
      'reserve.commentaire': c,
    });
  const selected = templates.find((t) => t.id === templateId);

  const addReserve = () => {
    if (!selected) return;
    void run(
      engine.updateDossier(d.id, {
        reserves: [...d.reserves, { textId: selected.id, comment: comment.trim() || null }],
      }),
    ).then(() => {
      setTemplateId('');
      setComment('');
    });
  };
  const openPdf = async (id = d.signature!.fileId!) => {
    try {
      window.open(await api.fileUrl(id), '_blank', 'noopener');
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <div className="card">
      <h2>Conditions, réserves & signature</h2>

      <h3>Conditions de la vente</h3>
      <p className="muted">
        Client : {CUSTOMER_TYPE_LABELS[customerType]} ·{' '}
        {w.applies
          ? `droit de rétractation de ${w.days} jours (${w.basis === 'L221-3' ? 'professionnel protégé, L221-3' : 'L221-18'})`
          : 'pas de droit de rétractation'}
      </p>
      <div className="form grid2">
        {form.showSaleChannel && (
        <Field label="Contrat conclu">
          <Select
            value={d.saleChannel}
            disabled={!editable}
            onChange={(e) =>
              void run(engine.updateDossier(d.id, { saleChannel: e.target.value as SaleChannel }))
            }
          >
            {SALE_CHANNELS.map((c) => (
              <option key={c} value={c}>
                {SALE_CHANNEL_LABELS[c]}
              </option>
            ))}
          </Select>
        </Field>
        )}
        {customerType === 'professional' && form.showProActivity && (
          <Field label="Nombre de salariés du client">
            <Input
              type="number"
              min={0}
              disabled={!editable}
              value={d.proEmployeeCount ?? ''}
              onChange={(e) =>
                void run(
                  engine.updateDossier(d.id, {
                    proEmployeeCount: e.target.value === '' ? null : Number(e.target.value),
                  }),
                )
              }
            />
          </Field>
        )}
      </div>
      {customerType === 'professional' && form.showProActivity && (
        <label className="check">
          <input
            type="checkbox"
            disabled={!editable}
            checked={!!d.proOutsideMainActivity}
            onChange={(e) =>
              void run(engine.updateDossier(d.id, { proOutsideMainActivity: e.target.checked }))
            }
          />
          L’objet du contrat n’entre pas dans le champ de l’activité principale du client
        </label>
      )}
      {w.applies && (
        <label className="check">
          <input
            type="checkbox"
            disabled={!editable}
            checked={d.earlyStartRequested}
            onChange={(e) => void run(engine.updateDossier(d.id, { earlyStartRequested: e.target.checked }))}
          />
          Le client demande que la prestation commence avant la fin du délai de rétractation (case C5 à la
          signature)
        </label>
      )}

      <h3>Réserves particulières</h3>
      {d.reserves.length === 0 ? (
        <Empty>Aucune réserve (facultatif).</Empty>
      ) : (
        <ul className="plain reserves">
          {d.reserves.map((r, i) => {
            const t = engine.get('legal_text', r.textId);
            return (
              <li key={`${r.textId}-${i}`}>
                <strong>{t?.title ?? 'Modèle désactivé'}</strong>
                <div className="pre">{t ? renderLegalText(t.body, vars(r.comment)) : ''}</div>
                {editable && (
                  <button
                    className="btn small ghost"
                    onClick={() =>
                      void run(engine.updateDossier(d.id, { reserves: d.reserves.filter((_, j) => j !== i) }))
                    }
                  >
                    Retirer
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {editable && templates.length > 0 && (
        <div className="form">
          <Field label="Ajouter une réserve">
            <Select
              value={templateId}
              onChange={(e) => {
                setTemplateId(e.target.value);
                setComment('');
              }}
            >
              <option value="">— choisir —</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                </option>
              ))}
            </Select>
          </Field>
          {selected && (
            <>
              <div className="pre reserve-preview">
                {renderLegalText(selected.body, vars(comment.trim() || null))}
              </div>
              {(selected.requiresComment || /reserve\.commentaire/.test(selected.body)) && (
                <Field label={`Commentaire${selected.requiresComment ? ' (obligatoire)' : ''}`}>
                  <Textarea value={comment} onChange={(e) => setComment(e.target.value)} />
                </Field>
              )}
              {selected.requiresPhoto && <p className="muted">Une photo est exigée avant la signature.</p>}
              <button
                className="btn"
                disabled={selected.requiresComment && !comment.trim()}
                onClick={addReserve}
              >
                Ajouter la réserve
              </button>
            </>
          )}
        </div>
      )}

      <h3>Signature du client</h3>
      {d.signature ? (
        <div className="signed-box">
          <p>
            ✅ Signé par <strong>{d.signature.signerName}</strong> le {formatDate(d.signature.signedAt)}{' '}
            {d.signature.method === 'portal' ? '(à distance)' : '(sur place)'} — le contrat est figé.
          </p>
          <div className="actions wrap">
            {d.signature.fileId && (
              <button className="btn" onClick={() => void openPdf()}>
                Bon de commande signé (PDF)
              </button>
            )}
            {d.signature.annexesFileId && (
              <button className="btn" onClick={() => void openPdf(d.signature!.annexesFileId!)}>
                Conditions générales et annexes (PDF)
              </button>
            )}
            {!d.signature.fileId && amountsVisibility(me.permissions).prices && (
              <span className="sync-tag">PDF disponible après synchronisation</span>
            )}
            {canVoid && (
              <button className="btn danger-outline" onClick={() => setVoiding(true)}>
                Annuler la signature
              </button>
            )}
            {canManage && (
              <>
                <button className="btn" onClick={() => setManaging('edit')}>
                  Corriger le signataire
                </button>
                <button className="btn danger-outline" onClick={() => setManaging('delete')}>
                  Supprimer la signature
                </button>
              </>
            )}
          </div>
        </div>
      ) : canSign ? (
        <div className="actions wrap">
          <button className="btn primary" onClick={() => setSigning(true)}>
            ✍ Récapitulatif et signature
          </button>
          {canQuote && (
            <button className="btn" onClick={() => setQuoting(true)}>
              📄 Envoyer le devis
            </button>
          )}
        </div>
      ) : (
        <>
          <Empty>Pas encore signé.</Empty>
          {canQuote && (
            <button className="btn" onClick={() => setQuoting(true)}>
              📄 Envoyer le devis
            </button>
          )}
        </>
      )}
      <ErrorBox message={error} />
      {signing && <SignDialog dossier={d} onClose={() => setSigning(false)} />}
      {quoting && <QuoteDialog dossier={d} onClose={() => setQuoting(false)} />}
      {voiding && <VoidDialog dossierId={d.id} onClose={() => setVoiding(false)} />}
      {managing && d.signature && (
        <ManageSignatureDialog
          mode={managing}
          dossierId={d.id}
          signature={d.signature}
          onClose={() => setManaging(null)}
        />
      )}
    </div>
  );
}

export function Recap({ c }: { c: ContractContent }) {
  const addr = (a: {
    line1: string | null;
    line2: string | null;
    postalCode: string | null;
    city: string | null;
  }) => [a.line1, a.line2, [a.postalCode, a.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return (
    <div className="recap">
      <p>
        <strong>{c.company.legalName}</strong> — {c.dossier.type}
      </p>
      <p>
        Client :{' '}
        <strong>
          {c.client.companyName ?? [c.client.firstName, c.client.lastName].filter(Boolean).join(' ')}
        </strong>{' '}
        ({CUSTOMER_TYPE_LABELS[c.customerType]})
        <br />
        Facturation : {addr(c.client.billing) || '—'}
        <br />
        Intervention : {addr(c.dossier.site) || 'identique'}
      </p>
      <table className="table">
        <tbody>
          {c.lines.map((l, i) => (
            <tr key={i}>
              <td>
                {l.label}
                {l.model && <span className="muted"> · {l.model}</span>}
                {'compareAtPriceHtCents' in l && !!l.compareAtPriceHtCents && (
                  <span className="muted">
                    {' '}
                    · au lieu de{' '}
                    <s>
                      {'compareAtPriceTtcCents' in l && l.compareAtPriceTtcCents
                        ? `${formatEuros(l.compareAtPriceTtcCents)} TTC`
                        : `${formatEuros(l.compareAtPriceHtCents)} HT`}
                    </s>
                  </span>
                )}
                {'discountHtCents' in l && !!l.discountHtCents && (
                  <span className="discount-tag"> Remise − {formatEuros(l.discountHtCents)} HT</span>
                )}
              </td>
              <td className="nowrap">
                {l.quantity} {UNIT_LABELS[l.unit]}
              </td>
              <td className="nowrap num">{formatEuros(l.totalHtCents)} HT</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="num">
        Total HT {formatEuros(c.totals.htCents)} · TVA {formatEuros(c.totals.vatCents)} ·{' '}
        <strong>Total TTC {formatEuros(c.totals.ttcCents)}</strong>
      </p>
      {c.lines.some((l) => 'highlight' in l && l.highlight) && (
        <div className="highlight-box">
          <strong>Garantie et entretien compris</strong>
          {c.lines
            .filter((l) => 'highlight' in l && l.highlight)
            .map((l, i) => (
              <div key={i}>
                <strong>
                  {l.label}
                  {l.quantity !== 1 && ` × ${l.quantity}`}
                </strong>
                <ul>
                  {l.optionSteps.filter(Boolean).map((x, j) => (
                    <li key={j}>{x}</li>
                  ))}
                </ul>
              </div>
            ))}
        </div>
      )}
      <p className="muted">
        {c.sale.withdrawal.applies
          ? `Droit de rétractation : ${c.sale.withdrawal.days} jours — formulaire joint au bon de commande.`
          : 'Pas de droit de rétractation pour ce contrat.'}
      </p>
      {c.reserves.length > 0 && (
        <>
          <h4>Réserves particulières</h4>
          {c.reserves.map((r, i) => (
            <div key={i} className="reserve-preview">
              <strong>{r.title}</strong>
              <div className="pre">{r.body}</div>
            </div>
          ))}
        </>
      )}
      {c.terms.map((t) => (
        <details key={t.id}>
          <summary>
            {t.title} (version {t.version}) — lire
          </summary>
          <div className="pre small terms">{t.body}</div>
        </details>
      ))}
      {c.privacy && (
        <details>
          <summary>{c.privacy.title} — lire</summary>
          <div className="pre small terms">{c.privacy.body}</div>
        </details>
      )}
    </div>
  );
}

function SignDialog({ dossier, onClose }: { dossier: DossierRecord; onClose(): void }) {
  const engine = useEngine();
  const { me } = useReady();
  const client = engine.get('client', dossier.clientId);
  const [signerName, setSignerName] = useState(client ? clientName(client) : '');
  const [quality, setQuality] = useState<'client' | 'representative'>('client');
  const [strokes, setStrokes] = useState<Strokes>([]);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const company = (me.tenant.settings as { legal?: CompanyLegal }).legal;
  const plan = useMemo(() => {
    try {
      return engine.signaturePlan(dossier.id, company);
    } catch {
      return null;
    }
  }, [engine, engine.version, dossier.id, company]);
  // cases obligatoires déjà cochées (le client décoche si besoin) ; la case facultative (offres commerciales) reste à cocher par le client
  const [consentIds, setConsentIds] = useState<string[]>(() =>
    (plan?.consents ?? []).filter((t) => t.required).map((t) => t.id),
  );
  const preview = useMemo(() => {
    try {
      return engine.contractPreview(dossier.id, { company, signerName, signerQuality: quality, consentIds });
    } catch (e) {
      return e instanceof Error ? e.message : null;
    }
  }, [engine, engine.version, dossier.id, company, signerName, quality, consentIds]);
  const consents = plan?.consents ?? [];
  const missing = consents.filter((t) => t.required && !consentIds.includes(t.id));
  const unreviewed = [...(plan?.terms ?? []), ...consents].some((t) => !t.reviewed);

  const submit = async () => {
    setError(null);
    setFields({});
    try {
      await engine.signDossier(dossier.id, {
        company,
        signerName,
        signerQuality: quality,
        consentIds,
        strokes,
      });
      onClose();
    } catch (e) {
      setError(errorMessage(e));
      setFields(fieldErrors(e));
    }
  };

  return (
    <Modal title="Récapitulatif et signature" onClose={onClose}>
      {unreviewed && (
        <p className="field-error legal-unreviewed">
          Textes non encore validés juridiquement : signature de test uniquement (refusée en production).
        </p>
      )}
      {typeof preview === 'string' ? <ErrorBox message={preview} /> : preview ? <Recap c={preview} /> : null}
      <div className="form">
        <h4>Cases à cocher par le client</h4>
        {consents.map((t) => (
          <label key={t.id} className="check consent">
            <input
              type="checkbox"
              checked={consentIds.includes(t.id)}
              onChange={(e) =>
                setConsentIds(e.target.checked ? [...consentIds, t.id] : consentIds.filter((x) => x !== t.id))
              }
            />
            <span>
              <strong>
                {t.title}
                {t.required ? ' *' : ' (facultatif)'}
              </strong>{' '}
              — {renderLegalText(t.body, legalVars(company))}
            </span>
          </label>
        ))}
        {fields.consentIds && <span className="field-error">Obligatoire : {fields.consentIds}</span>}
        <div className="grid2">
          <Field label="Nom du signataire">
            <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} />
          </Field>
          <Field label="Qualité">
            <Select
              value={quality}
              onChange={(e) => setQuality(e.target.value as 'client' | 'representative')}
            >
              {Object.entries(SIGNER_QUALITY_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <span className="field-label">Signature du client</span>
        <SignaturePad value={strokes} onChange={setStrokes} />
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={() => setStrokes([])} disabled={!strokes.length}>
            Effacer
          </button>
          <button className="btn" onClick={onClose}>
            Annuler
          </button>
          <button
            className="btn primary"
            disabled={!strokes.length || !signerName.trim() || missing.length > 0}
            onClick={() => void submit()}
          >
            Valider la signature
          </button>
        </div>
        {missing.length > 0 && (
          <p className="muted small">Le bouton s’active quand les cases obligatoires (*) sont cochées.</p>
        )}
      </div>
    </Modal>
  );
}

function VoidDialog({ dossierId, onClose }: { dossierId: string; onClose(): void }) {
  const engine = useEngine();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal title="Annuler la signature" onClose={onClose}>
      <div className="form">
        <p>
          Le contrat redevient modifiable et devra être signé à nouveau. La signature annulée reste dans
          l’historique.
        </p>
        <Field label="Motif">
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Retour
          </button>
          <button
            className="btn danger-outline"
            disabled={reason.trim().length < 3}
            onClick={() =>
              engine
                .voidSignature(dossierId, reason)
                .then(onClose)
                .catch((e) => setError(errorMessage(e)))
            }
          >
            Annuler la signature
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** Administrateur : corriger le nom / la qualité du signataire, ou supprimer la signature et ses PDF (motif tracé). */
function ManageSignatureDialog({
  mode,
  dossierId,
  signature,
  onClose,
}: {
  mode: 'edit' | 'delete';
  dossierId: string;
  signature: { id: string; signerName: string };
  onClose(): void;
}) {
  const engine = useEngine();
  const { api } = useReady();
  const [name, setName] = useState(signature.signerName);
  const [quality, setQuality] = useState<'client' | 'representative'>('client');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const run = async () => {
    setError(null);
    try {
      if (mode === 'edit')
        await api.request('PATCH', `/v1/dossiers/${dossierId}/signatures/${signature.id}`, {
          signerName: name,
          signerQuality: quality,
        });
      else
        await api.request('POST', `/v1/dossiers/${dossierId}/signatures/${signature.id}/delete`, { reason });
      await engine.sync();
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  return (
    <Modal title={mode === 'edit' ? 'Corriger le signataire' : 'Supprimer la signature'} onClose={onClose}>
      <div className="form">
        {mode === 'edit' ? (
          <>
            <Field label="Nom du signataire">
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Qualité">
              <Select value={quality} onChange={(e) => setQuality(e.target.value as typeof quality)}>
                <option value="client">Le client</option>
                <option value="representative">Son représentant</option>
              </Select>
            </Field>
            <p className="muted">Le PDF déjà signé n’est pas modifié.</p>
          </>
        ) : (
          <>
            <p>
              La signature et ses PDF (bon de commande, conditions et annexes) sont supprimés, quel que soit
              le statut du dossier. Le dossier pourra être signé à nouveau.
            </p>
            <Field label="Motif">
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
          </>
        )}
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Retour
          </button>
          <button
            className={`btn ${mode === 'delete' ? 'danger' : 'primary'}`}
            disabled={mode === 'edit' ? name.trim().length < 2 : reason.trim().length < 3}
            onClick={() => void run()}
          >
            {mode === 'edit' ? 'Enregistrer' : 'Supprimer la signature'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Devis non signé : le bon de commande sans signature (cases vierges, « ne vaut pas commande »), envoyé au client
 * pour qu'il voie l'offre. Le PDF est conservé dans les documents du dossier ; le lien vaut 30 jours.
 */
function QuoteDialog({ dossier, onClose }: { dossier: DossierRecord; onClose(): void }) {
  const engine = useEngine();
  const { api } = useReady();
  const client = engine.get('client', dossier.clientId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{
    channel: 'email' | 'sms' | 'link';
    url: string;
    share: { whatsapp: string; sms: string; email: string };
  } | null>(null);
  const send = async (channel: 'email' | 'sms' | 'link') => {
    setBusy(true);
    setError(null);
    try {
      // le devis est établi par le serveur : on lui envoie d'abord les dernières modifications
      await engine.sync();
      if (engine.pendingCount > 0) throw new Error('Synchronisez d’abord le dossier (connexion nécessaire)');
      const r = await api.request<{ url: string; share: { whatsapp: string; sms: string; email: string } }>(
        'POST',
        `/v1/dossiers/${dossier.id}/quote`,
        { channel },
      );
      setDone({ channel, ...r });
      void engine.sync(); // la copie du devis apparaît dans les documents du dossier
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Envoyer le devis" onClose={onClose}>
      <div className="form">
        {!done ? (
          <>
            <p>
              Le client reçoit le devis en PDF (prix, produits, conditions) <strong>sans l’avoir signé</strong> : il ne vaut pas
              commande. Une copie est gardée dans les documents du dossier. Le lien reste valable 30 jours.
            </p>
            <div className="actions wrap">
              <button className="btn primary" disabled={busy || !client?.email} onClick={() => void send('email')}>
                ✉ Par e-mail{client?.email ? ` (${client.email})` : ' — pas d’e-mail'}
              </button>
              <button className="btn primary" disabled={busy || !client?.phone} onClick={() => void send('sms')}>
                💬 Par SMS{client?.phone ? ` (${client.phone})` : ' — pas de téléphone'}
              </button>
              <button className="btn" disabled={busy} onClick={() => void send('link')}>
                🔗 Lien à partager (WhatsApp…)
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="success-text" role="status">
              {done.channel === 'link'
                ? 'Devis prêt : partagez-le avec le client.'
                : `Devis envoyé par ${done.channel === 'email' ? 'e-mail' : 'SMS'}.`}
            </p>
            <div className="actions wrap">
              <a className="btn" href={done.url} target="_blank" rel="noreferrer">
                Voir le devis
              </a>
              <a className="btn" href={done.share.whatsapp} target="_blank" rel="noreferrer">
                WhatsApp
              </a>
              <a className="btn" href={done.share.sms}>
                SMS depuis mon téléphone
              </a>
              <a className="btn" href={done.share.email}>
                E-mail depuis ma messagerie
              </a>
              <button className="btn" onClick={() => void navigator.clipboard?.writeText(done.url)}>
                Copier le lien
              </button>
            </div>
          </>
        )}
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Fermer
          </button>
        </div>
      </div>
    </Modal>
  );
}
